import {
  DEFAULT_USER_SETTINGS,
  POST_TYPE_OPTIONS,
  TOPIC_OPTIONS,
  TOPIC_STYLES
} from '../constants/hixrs';

export function getUserSettings(profile = {}) {
  const saved = profile?.settings || {};
  return {
    privacy: { ...DEFAULT_USER_SETTINGS.privacy, ...(saved.privacy || {}) },
    preferences: { ...DEFAULT_USER_SETTINGS.preferences, ...(saved.preferences || {}) },
    notifications: { ...DEFAULT_USER_SETTINGS.notifications, ...(saved.notifications || {}) }
  };
}

export function getTopicKey(topic = '') {
  const lower = topic.toLowerCase();
  return TOPIC_OPTIONS.find(t => lower.includes(t.key))?.key || 'default';
}

export function getTopicLabel(topic = '') {
  const key = getTopicKey(topic);
  return TOPIC_OPTIONS.find(t => t.key === key)?.label || (topic || 'General').replace(/[^a-z0-9 ]/gi, '').trim() || 'General';
}

export function getTopicStyle(topic = '') {
  return TOPIC_STYLES[getTopicKey(topic)] || TOPIC_STYLES.default;
}

export function getTopicOptionByKey(key) {
  return TOPIC_OPTIONS.find(topic => topic.key === key);
}

export function postMatchesTopic(post, topicKey) {
  if (!topicKey || topicKey === 'all') return true;
  if (topicKey === 'hixs') return !isOpenThought(post);
  if (topicKey === 'thoughts') return isOpenThought(post);
  if (topicKey === 'hotmix') return true;
  if (topicKey.startsWith('custom:')) {
    return getTopicLabel(post.topic).toLowerCase() === topicKey.replace('custom:', '').toLowerCase();
  }
  return getTopicKey(post.topic) === topicKey;
}

export function getCustomTopicOptions(posts = []) {
  const custom = new Map();
  posts.forEach(post => {
    const label = getTopicLabel(post.topic);
    if (!label || label === 'General') return;
    if (getTopicKey(post.topic) !== 'default') return;
    custom.set(label.toLowerCase(), label);
  });
  return Array.from(custom.values()).slice(0, 12).map(label => ({
    key: `custom:${label}`,
    label
  }));
}

export function getPostTypeOption(postType = 'take') {
  return POST_TYPE_OPTIONS.find(type => type.key === postType) || POST_TYPE_OPTIONS[1];
}

export function isOpenThought(post = {}) {
  return post?.postType === 'thought';
}

export function clampPct(value, fallback = 50) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(0, Math.min(100, Math.round(number)));
}

export function normalizeSide(side) {
  return side === 'a' || side === 'b' ? side : null;
}

export function getPostLeaderFromPct(pctA, pctB, totalVotes = 0) {
  if (!totalVotes || pctA === pctB) return 'tie';
  return pctA > pctB ? 'a' : 'b';
}

export function calculateLockInCounters(votesAInput = 0, votesBInput = 0) {
  const votesA = Math.max(0, Number(votesAInput || 0));
  const votesB = Math.max(0, Number(votesBInput || 0));
  const totalVotes = votesA + votesB;
  const pctA = totalVotes > 0 ? Math.round((votesA / totalVotes) * 100) : 50;
  const pctB = 100 - pctA;
  const heat = 100 - Math.abs(pctA - 50) * 2;
  const currentLeader = getPostLeaderFromPct(pctA, pctB, totalVotes);
  return { totalVotes, votesA, votesB, pctA, pctB, heat, currentLeader };
}

export function getPostLeader(post = {}) {
  const counters = calculateLockInCounters(post.votesA, post.votesB);
  if (counters.totalVotes > 0) return counters.currentLeader;
  return getPostLeaderFromPct(clampPct(post.pctA), clampPct(post.pctB), post.totalVotes || 0);
}

export function getPostSplit(post = {}) {
  const votesA = Number(post.votesA || 0);
  const votesB = Number(post.votesB || 0);
  const countedTotal = votesA + votesB;

  if (countedTotal > 0) {
    const pctA = clampPct((votesA / countedTotal) * 100);
    return { pctA, pctB: 100 - pctA };
  }

  const pctA = clampPct(post.pctA);
  return { pctA, pctB: 100 - pctA };
}

export function getPostDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatRelativeTime(value) {
  const date = getPostDate(value);
  if (!date) return 'Just now';
  const diffSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (diffSeconds < 60) return 'Just now';
  const units = [
    { label: 'y', seconds: 60 * 60 * 24 * 365 },
    { label: 'mo', seconds: 60 * 60 * 24 * 30 },
    { label: 'd', seconds: 60 * 60 * 24 },
    { label: 'h', seconds: 60 * 60 },
    { label: 'm', seconds: 60 },
  ];
  const unit = units.find(item => diffSeconds >= item.seconds);
  return `${Math.floor(diffSeconds / unit.seconds)}${unit.label} ago`;
}

export function getCommentLabel(count = 0) {
  return Number(count) === 1 ? 'comment' : 'comments';
}

export function getViewerDisplayName(user, profile) {
  return profile?.displayName || user?.displayName || 'Anonymous';
}

export function getViewerUsername(user, profile) {
  return (profile?.username || user?.email?.split('@')[0] || '').toLowerCase();
}

export function userOwnsPost(post, user, profile) {
  if (!user || !post) return false;
  if (post.authorId) return post.authorId === user.uid;

  const displayName = getViewerDisplayName(user, profile);
  const username = getViewerUsername(user, profile);
  const postHandle = (post.authorHandle || '').replace(/^@/, '').toLowerCase();

  return Boolean(
    post.authorName === displayName ||
    (postHandle && username && postHandle === username)
  );
}

export function userOwnsThreadItem(item, user, profile) {
  if (!user || !item) return false;
  if (item.userId) return item.userId === user.uid;
  return item.user === getViewerDisplayName(user, profile);
}

export function findUserProfile(users = [], uid) {
  if (!uid) return null;
  return users.find(user => user.uid === uid || user.id === uid) || null;
}

export function getProfileIdentity(target = {}, users = []) {
  const uid = target.uid || target.userId || target.authorId || '';
  const saved = findUserProfile(users, uid) || {};
  const displayName = saved.displayName || target.displayName || target.authorName || target.user || 'Hix user';
  const rawUsername = saved.username || target.username || target.authorHandle || '';
  const username = rawUsername ? rawUsername.replace(/^@/, '') : '';

  return {
    uid,
    saved,
    displayName,
    username,
    handle: username ? `@${username}` : '@hixr',
    photoURL: saved.photoURL || target.photoURL || target.authorPhoto || target.userPhoto || '',
    bannerURL: saved.bannerURL || target.bannerURL || '',
    bio: saved.bio || 'Building an opinion identity on Hixrs.',
    following: Array.isArray(saved.following) ? saved.following : []
  };
}
